import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Copy, Info, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";

import { salesCategoriesGetAll } from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

import { useEmployeePermissions } from "../../../../hooks/use-employee-permissions";
import { taxCategoryDefault } from "../../../../utils/tax-category";
import { ImageDropzone } from "../../../shared/image-dropzone";
import { Button } from "../../../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../../../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { CloseDialogButton } from "./close-dialog-button";
import {
  CategoriesSection,
  OnlineOrderingSection,
  ReportingSection,
} from "./item-form-sections";
import type {
  CategoryForMenu,
  ItemDataForForm,
  ItemFormData,
  ItemMutationData,
  LocationForForm,
  ModifierGroupForForm,
  TaxCategoryForForm,
} from "./item-form-types";
import { itemsCloneAndUpdateRef, itemsUpdateRef } from "./item-form-types";
import { buildMutationData } from "./item-form-utils";
import { ModifiersSection } from "./item-modifiers-section";
import { PriceInput } from "./item-price-input";
import { SharedItemDialog } from "./shared-item-dialog";

export const ItemDetailsForm = ({
  menuId,
  itemData,
  categories,
  taxCategories,
  modifierGroups,
  locations,
  userId: _userId,
  // V4.1: Route-based category creation props
  newSalesCategoryId,
  newTaxCategoryId,
  onCreateSalesCategory,
  onCreateTaxCategory,
  onClearSearchParams,
}: {
  menuId: string;
  itemData: ItemDataForForm;
  categories: CategoryForMenu[];
  taxCategories: TaxCategoryForForm[];
  modifierGroups: ModifierGroupForForm[];
  locations: LocationForForm[];
  userId: string;
  // V4.1: Route-based category creation props
  newSalesCategoryId?: string;
  newTaxCategoryId?: string;
  onCreateSalesCategory: (name: string) => void;
  onCreateTaxCategory: (name: string) => void;
  onClearSearchParams: () => void;
}) => {
  const navigate = useNavigate();

  const taxCategoriesList = [taxCategoryDefault, ...taxCategories];

  const salesCategories = useQuery(salesCategoriesGetAll) ?? [];
  const { hasPermission } = useEmployeePermissions();
  const canEditStockStatus = hasPermission("editStockStatus");

  const form = useForm<ItemFormData, unknown, ItemFormData>({
    defaultValues: {
      name: "",
      shortName: "",
      imageUrl: "",
      description: "",
      price: "0.00",
      isOutOfStock: false,
      availableOnline: true,
      onlinePrice: "",
      categories: [],
      modifierGroups: [],
      useCategoryModifiers: false,
      devicesIds: [],
      taxCategoryId: "0",
      salesCategoryId: null,
    },
  });

  useEffect(() => {
    if (itemData) {
      form.reset({
        name: itemData.name,
        shortName: itemData.shortName,
        imageUrl: itemData.imageUrl ?? null,
        description: itemData.description,
        isOutOfStock: itemData.isOutOfStock,
        availableOnline: itemData.availableOnline !== false,
        onlinePrice: itemData.onlinePrice
          ? itemData.onlinePrice.toFixed(2)
          : "",
        useCategoryModifiers: itemData.useCategoryModifiers,
        salesCategoryId: itemData.salesCategoryId ?? null,
        categories: itemData.categoryRelations.map((c) =>
          toConvexTableId("categories", c.category._id)
        ),
        devicesIds: [],
        modifierGroups: itemData.modifierGroupRelations.map(
          (m) => m.modifierGroupId
        ),
        taxCategoryId: itemData.taxCategoryId ?? "0",
        price:
          typeof itemData.price === "object"
            ? Object.fromEntries(
                Object.entries(itemData.price).map(([locationId, price]) => [
                  locationId,
                  (typeof price === "string"
                    ? Number.parseFloat(price)
                    : price
                  ).toFixed(2),
                ])
              )
            : (typeof itemData.price === "string"
                ? Number.parseFloat(itemData.price)
                : itemData.price
              ).toFixed(2),
      });
    }
  }, [itemData, form.reset, form]);

  // V4.1: Handle search params from child category creation routes
  useEffect(() => {
    if (newSalesCategoryId) {
      form.setValue(
        "salesCategoryId",
        toConvexTableId("salesCategories", newSalesCategoryId)
      );
      onClearSearchParams();
    }
  }, [newSalesCategoryId, form, onClearSearchParams]);

  useEffect(() => {
    if (newTaxCategoryId) {
      form.setValue(
        "taxCategoryId",
        toConvexTableId("taxCategories", newTaxCategoryId)
      );
      onClearSearchParams();
    }
  }, [newTaxCategoryId, form, onClearSearchParams]);

  const onGoBack = () => {
    form.reset();
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const queryClient = useQueryClient();

  const updateItemMutation = useMutation({
    mutationFn: (data: { itemId: Id<"items">; data: ItemMutationData }) =>
      convexClient.mutation(itemsUpdateRef, data),
    onSuccess: (_result, variables) => {
      ProductEvents.menuItemUpdated(variables.itemId);
      void queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });

  const cloneAndUpdateItemMutation = useMutation({
    mutationFn: (data: {
      id: Id<"items">;
      data: ItemMutationData;
      categoryIds: Id<"categories">[];
    }) => convexClient.mutation(itemsCloneAndUpdateRef, data),
    onSuccess: (result, variables) => {
      if (result?.id) {
        ProductEvents.menuItemCreated(
          result.id,
          variables.categoryIds[0] ?? ""
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });

  const handleFormSubmit = async (data: ItemFormData) => {
    if (itemData.categoryRelations.length > 1) {
      setUpdatingSharedItemStatus("confirm");
      return;
    }
    if (updateItemMutation.isPending) {
      return;
    }

    try {
      await updateItemMutation.mutateAsync({
        itemId: itemData.id,
        data: buildMutationData(data),
      });
      onGoBack();
    } catch (error) {
      if (
        error instanceof Error &&
        error.message?.startsWith("Item name already exists")
      ) {
        form.setError("name", {
          message: error.message,
          type: "value",
        });
      }
    }
  };

  const [updatingSharedItemStatus, setUpdatingSharedItemStatus] = useState<
    "confirm" | "select-categories" | null
  >(null);

  const [menuCategoriesToCopyItem, setMenuCategoriesToCopyItem] = useState<
    string[]
  >([]);

  return (
    <>
      <SharedItemDialog
        categories={categories}
        cloneAndUpdateItemMutation={cloneAndUpdateItemMutation}
        form={form}
        itemData={itemData}
        menuCategoriesToCopyItem={menuCategoriesToCopyItem}
        onGoBack={onGoBack}
        setMenuCategoriesToCopyItem={setMenuCategoriesToCopyItem}
        setUpdatingSharedItemStatus={setUpdatingSharedItemStatus}
        updateItemMutation={updateItemMutation}
        updatingSharedItemStatus={updatingSharedItemStatus}
      />
      <Dialog
        onOpenChange={(value: boolean) => {
          if (!value) {
            void navigate({ to: ".." });
          }
        }}
        open
      >
        <DialogContent
          className="h-full max-h-dvh w-full max-w-full overflow-y-hidden rounded-none"
          noCloseButton
        >
          <Form {...form}>
            <form
              className="h-[calc(100vh-142px)]"
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <div className="bg-muted relative flex h-18 items-center justify-center">
                <DialogTitle className="sr-only">Edit item</DialogTitle>
                <h2 className="h4 text-foreground text-center">Edit item</h2>
                <CloseDialogButton
                  onGoBack={onGoBack}
                  onSubmit={form.handleSubmit(handleFormSubmit)}
                />
              </div>

              <div className="relative flex h-full w-full justify-center overflow-y-auto pb-27">
                <div className="flex h-auto w-full max-w-4xl flex-col">
                  <div className="space-y-5">
                    <div className="space-y-1">
                      <h2 className="subtitle-sm text-foreground mt-5">
                        Details
                      </h2>
                      <h3 className="body-sm text-muted-foreground">
                        Customize names, price, description and availability
                      </h3>
                    </div>

                    <div className="flex">
                      <div className="mr-5 w-full space-y-5">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Item Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="w-full space-y-2">
                          <Label className="flex items-center gap-1">
                            Printer / Display name
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="cursor-help">
                                  <Info className="size-4" />
                                  <span className="sr-only">
                                    Hover for additional information
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>
                                    Short name for order tickets and kitchen
                                    displays
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <FormField
                            control={form.control}
                            name="shortName"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="imageUrl"
                        render={({ field }) => (
                          <ImageDropzone
                            onValueChange={field.onChange}
                            value={field.value ?? null}
                          />
                        )}
                      />
                    </div>

                    <div className="space-y-1">
                      <h3 className="subtitle-sm text-foreground">
                        Printing & Display
                      </h3>
                      <p className="body-sm text-muted-foreground">
                        Configure where this item is printed or displayed
                      </p>
                    </div>

                    <div className="space-y-2">
                      <FormField
                        control={form.control}
                        name="price"
                        render={({ field }) => (
                          <>
                            <PriceInput
                              locations={locations}
                              onChange={field.onChange}
                              value={field.value ?? ""}
                            />
                            <FormMessage />
                          </>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description / Ingredients</FormLabel>
                          <FormControl>
                            <TextareaAutosize
                              className="border-border focus-visible:border-ring min-h-20 w-full rounded-2xl border px-5 py-3 outline-0"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <OnlineOrderingSection
                      canEditStockStatus={canEditStockStatus}
                      form={form}
                    />
                  </div>

                  <ReportingSection
                    form={form}
                    onCreateSalesCategory={onCreateSalesCategory}
                    onCreateTaxCategory={onCreateTaxCategory}
                    salesCategories={salesCategories}
                    taxCategories={taxCategories}
                    taxCategoriesList={taxCategoriesList}
                    taxCategoryDefault={taxCategoryDefault}
                  />

                  <CategoriesSection
                    categories={categories}
                    form={form}
                    itemData={itemData}
                  />

                  <ModifiersSection
                    form={form}
                    modifierGroups={modifierGroups}
                  />

                  <div className="mb-27 pb-12" />
                </div>

                <div className="border-border fixed inset-x-0 bottom-0 left-0 inline-flex h-18 justify-between border-t p-3">
                  <div className="space-x-3">
                    {/* V4.1: Route-based navigation for duplicate item */}
                    <Button
                      className="space-x-1.5"
                      onClick={(e) => {
                        e.preventDefault();
                        void navigate({
                          to: `/menus/${menuId}/items/new`,
                          search: { duplicate: itemData.id },
                        });
                      }}
                      type="button"
                      variant="secondary"
                    >
                      <Copy className="size-4" />
                      <p className="label text-foreground">
                        Create Similar Item
                      </p>
                    </Button>
                    {/* V4.1: Route-based delete for URL context tracking */}
                    <Button
                      className="space-x-1.5"
                      onClick={(e) => {
                        e.preventDefault();
                        void navigate({
                          to: `/menus/${menuId}/items/${itemData.id}/delete`,
                        });
                      }}
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="size-4" />
                      <p className="label text-foreground">Delete Item</p>
                    </Button>
                  </div>
                  <Button
                    disabled={
                      !form.formState.isDirty || updateItemMutation.isPending
                    }
                    type="submit"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
